import React, {useMemo} from 'react';
import {CustomTextField} from 'app/components/match/liveResult/CustomNumberTextField';
import {GridElement} from 'app/components/match/liveResult/updateScorer/sharedStyles';
import {Inputs, Player} from 'app/components/match/liveResult/updateScorer/sharedTypes';
import {Autocomplete, MenuItem, TextFieldProps} from '@mui/material';
import {Control, Controller, FieldError} from 'react-hook-form';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
`;

const JerseyNumber = styled.div`
  flex: 0 0 28px;
  text-align: center;
  padding-right: 4px;
`;

const Name = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface PlayerSelectProps {
  error: FieldError | undefined;
  players: Player[];
  show: boolean;
  control: Control<Inputs, any>;
  name: keyof Inputs;
  defaultValue: number | 'unbekannt' | '';
}

type Props = PlayerSelectProps & Omit<TextFieldProps, 'error'>;

export default function PlayerSelect({players, show, error, control, defaultValue, ...props}: Props) {
  const options: Player[] = useMemo(
    () => [{id: 'unbekannt', jerseyNumber: 0, lastName: 'Unbekannt', firstName: ''}, ...players],
    [players]
  );

  const initialValue = useMemo(() => {
    return options.find(player => player.id === defaultValue) ?? null;
  }, [defaultValue, options]);

  return show && players.length ? (
    <GridElement>
      <Controller
        control={control}
        name={props.name}
        render={({field: {onChange}}) => (
          <Autocomplete
            onChange={(event, item) => {
              onChange(item?.id ?? '');
            }}
            id='player-select'
            options={options}
            fullWidth={true}
            defaultValue={initialValue}
            getOptionLabel={option =>
              `${option.jerseyNumber ? `(${option.jerseyNumber}) ` : ''}${option.lastName} ${option.firstName}`
            }
            renderOption={(props, option: Player, state) => (
              <MenuItem key={option.id} value={option.id} {...props}>
                <Container>
                  {option.jerseyNumber ? <JerseyNumber>{option.jerseyNumber}</JerseyNumber> : <JerseyNumber />}
                  <Name>{`${option.lastName} ${option.firstName}`}</Name>
                </Container>
              </MenuItem>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <CustomTextField
                {...params}
                {...props}
                error={!!error}
                variant='outlined'
                size='small'
                helperText={error?.message ?? ''}
              />
            )}
          />
        )}
      />
    </GridElement>
  ) : (
    <GridElement />
  );
}
